import { useCallback, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useTranslation } from "next-i18next";
import { GoArrowLeft, GoArrowRight } from "react-icons/go";
import { MdDone, MdOutlineKeyboardDoubleArrowDown } from "react-icons/md";

const ACCENT = "#FE3D67";

const CARD_BORDER = {
  background: "linear-gradient(185deg, rgba(29, 54, 118, 0.5) -1.41%, #3A9FDA 85.85%)",
  WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
  WebkitMaskComposite: "xor",
  maskComposite: "exclude",
};

const STEP_COUNT = 4;

const PROGRAM_KEYS = [
  {
    id: "full-stack",
    route: "/programs/full-stack",
    title: "programs-page.hero-slide-down-column1-title1",
    duration: "programs-page.programs-card-fullstack-duration",
    modules: "programs-page.programs-card-fullstack-modules",
  },
  {
    id: "front-end",
    route: "/programs/front-end",
    title: "programs-page.hero-slide-down-column2-title1",
    duration: "programs-page.programs-card-frontend-duration",
    modules: "programs-page.programs-card-frontend-modules",
  },
  {
    id: "back-end",
    route: "/programs/back-end",
    title: "programs-page.hero-slide-down-column3-title1",
    duration: "programs-page.programs-card-backend-duration",
    modules: "programs-page.programs-card-backend-modules",
  },
  {
    id: "software-engineering",
    route: "/programs/software-engineering",
    title: "programs-page.hero-slide-down-column4-title1",
    duration: "programs-page.programs-card-software-duration",
    modules: "programs-page.programs-card-software-modules",
  },
];

const useCardSlider = () => {
  const sliderRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  const updateScrollState = useCallback(() => {
    const el = sliderRef.current;
    if (!el) return;

    const maxScroll = el.scrollWidth - el.clientWidth;
    const left = el.scrollLeft;

    setCanScrollLeft(left > 2);
    setCanScrollRight(left < maxScroll - 2);
    setScrollProgress(maxScroll > 0 ? Math.min(1, Math.max(0, left / maxScroll)) : 0);
  }, []);

  useEffect(() => {
    updateScrollState();

    const el = sliderRef.current;
    if (!el) return;

    el.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);

    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [updateScrollState]);

  const scrollByCards = useCallback((direction) => {
    const el = sliderRef.current;
    if (!el) return;

    const positions = Array.from(el.querySelectorAll("[data-program-card]")).map(
      (card) => card.offsetLeft,
    );
    if (!positions.length) return;

    const current = el.scrollLeft;

    if (direction === "right") {
      const next = positions.find((pos) => pos > current + 2);
      el.scrollTo({ left: next ?? positions.at(-1), behavior: "smooth" });
      return;
    }

    const previous = [...positions].reverse().find((pos) => pos < current - 2);
    el.scrollTo({ left: previous ?? 0, behavior: "smooth" });
  }, []);

  return { sliderRef, canScrollLeft, canScrollRight, scrollProgress, scrollByCards };
};

const StepCard = ({ step, label, title, description }) => (
  <article className="bg-[#F5F7F9] rounded-[12px] p-[20px]">
    <p className="text-main-pink text-[16px] font-[600] mb-[12px] md:mb-[16px]">
      {label} {step}
    </p>
    <h3 className="text-transparent bg-clip-text bg-gradient-to-b from-[#091F59] to-[#1A3884] text-[16px] md:text-[20px] font-[600] mb-[12px] md:mb-[20px]">
      {title}
    </h3>
    <p className="text-[16px] md:text-[20px] font-[400] text-[#202020CC] leading-relaxed">
      {description}
    </p>
  </article>
);

const SliderProgramCard = ({ title, features, route, buttonLabel }) => (
  <article
    data-program-card
    className="flex-none w-full xs:w-[340px] lg:w-[400px] xl:w-[480px] snap-start relative rounded-[24px] overflow-hidden bg-[linear-gradient(80.42deg,rgba(255,255,255,0.08)_25.25%,rgba(83,84,108,0.1)_98.05%)] backdrop-blur-[16px]"
    style={{ "--program-color": ACCENT }}
  >
    <div className="absolute inset-0 rounded-[24px] p-[1.2px] pointer-events-none" style={CARD_BORDER} />

    <div className="px-[16px] py-[40px] flex gap-[20px] relative">
      <div className="absolute opacity-60 w-[1.5px] left-[36px] top-[64px] md:top-[72px] bottom-[24px] md:bottom-[32px] bg-gradient-to-b from-[var(--program-color)] to-[rgba(255,255,255,0.01)]" />

      <div className="min-w-[40px] h-[40px] rounded-full flex items-center justify-center flex-shrink-0 bg-[var(--program-color)] relative z-10">
        <MdOutlineKeyboardDoubleArrowDown className="text-white w-[24px] h-[24px] md:w-[28px] md:h-[28px]" />
      </div>

      <div className="space-y-[12px] md:space-y-[20px] flex-1 relative z-10 min-w-0">
        <h3 className="text-[16px] sm:text-[20px] md:text-[24px] font-[600] text-[#CAD1E9] mb-[12px] px-[15px] text-center">
          {title}
        </h3>

        {features.map((feature) => (
          <div key={feature} className="flex items-center gap-[12px]">
            <span className="min-w-[20px] min-h-[20px] md:min-w-[24px] md:min-h-[24px] rounded-full flex items-center justify-center flex-shrink-0 bg-[var(--program-color)]/40">
              <MdDone className="w-[14px] h-[14px] md:w-[16px] md:h-[16px] text-[var(--program-color)]" />
            </span>
            <span className="text-[14px] md:text-[16px] font-[400] text-[#CAC6DD]">{feature}</span>
          </div>
        ))}

        <Link
          href={route}
          className="inline-flex w-full md:w-[170px] h-[32px] md:h-[48px] items-center justify-center rounded-full bg-[var(--program-color)] text-white text-[14px] md:text-[16px] font-[500] transition-colors duration-300 hover:bg-white hover:text-[var(--program-color)] mt-[20px]"
        >
          {buttonLabel}
        </Link>
      </div>
    </div>
  </article>
);

const SliderControls = ({ scrollProgress, canScrollLeft, canScrollRight, onScroll }) => (
  <div className="flex items-center justify-between gap-[16px] mt-[14px]">
    <div className="flex-1 max-w-[90%] h-[6px] rounded-full bg-[rgba(255,255,255,0.14)] relative overflow-hidden">
      <div
        className="absolute top-0 bottom-0 w-1/2 rounded-full bg-white"
        style={{ transform: `translateX(${scrollProgress * 100}%)` }}
      />
    </div>

    <div className="flex items-center gap-[16px]">
      {["left", "right"].map((direction) => (
        <button
          key={direction}
          type="button"
          onClick={() => onScroll(direction)}
          disabled={direction === "left" ? !canScrollLeft : !canScrollRight}
          aria-label={`Scroll ${direction}`}
          className="w-[44px] h-[44px] rounded-full bg-[linear-gradient(80.42deg,rgba(255,255,255,0.08)_25.25%,rgba(83,84,108,0.1)_98.05%)] text-white flex items-center justify-center transition-opacity disabled:opacity-40"
        >
          {direction === "left" ? (
            <GoArrowLeft className="w-[18px] h-[18px]" />
          ) : (
            <GoArrowRight className="w-[18px] h-[18px]" />
          )}
        </button>
      ))}
    </div>
  </div>
);

const AvailablePrograms = () => {
  const { t } = useTranslation();
  const { sliderRef, canScrollLeft, canScrollRight, scrollProgress, scrollByCards } = useCardSlider();

  const stepLabel = t("programs-page.available-programs-step-label");
  const scheduleFeature = t("programs-page.programs-card-schedule-description");
  const buttonLabel = t("programs-page.programs-card-button");

  const steps = Array.from({ length: STEP_COUNT }, (_, index) => {
    const step = index + 1;
    return {
      step,
      title: t(`programs-page.available-programs-step-${step}-title`),
      description: t(`programs-page.available-programs-step-${step}-description`),
    };
  });

  const programs = PROGRAM_KEYS.map(({ id, route, title, duration, modules }) => ({
    id,
    route,
    title: t(title),
    features: [t(duration), t(modules), scheduleFeature],
  }));

  return (
    <>
      <section className="w-full bg-white py-[80px] md:pt-[100px] md:pb-[60px]">
        <div className="max-w-[90%] mx-auto">
          <header className="text-center mb-[32px] md:mb-[24px]">
            <h1 className="text-[32px] md:text-[48px] lg:text-[60px] font-[600] mb-[12px] text-transparent bg-clip-text bg-gradient-to-b from-[#091F59] to-[#1A3884]">
              {t("programs-page.available-programs-title")}
            </h1>
            <p className="text-[16px] md:text-[20px] font-[400] text-[#222222] leading-relaxed mx-auto">
              {t("programs-page.available-programs-description")}
            </p>
          </header>

          <div>
            <h2 className="text-center md:text-left text-[32px] font-[500] text-main-pink mb-[20px] md:mb-[32px]">
              {t("programs-page.available-programs-steps-title")}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-[20px] md:gap-[32px]">
              {steps.map(({ step, title, description }) => (
                <StepCard
                  key={step}
                  step={step}
                  label={stepLabel}
                  title={title}
                  description={description}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-gradient-to-b from-[#091F59] to-[#1A3884] py-[60px] md:py-[100px] rounded-[40px] md:rounded-[64px]">
        <div className="max-w-[90%] mx-auto">
          <h2 className="text-[32px] md:text-[48px] lg:text-[60px] font-[500] text-white mb-[24px]">
            {t("programs-page.programs-title")}
          </h2>

          <div className="relative">
            <div
              ref={sliderRef}
              className="flex gap-[24px] md:gap-[32px] overflow-x-auto scroll-smooth snap-x snap-mandatory pb-[18px] [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              {programs.map((program) => (
                <SliderProgramCard key={program.id} {...program} buttonLabel={buttonLabel} />
              ))}
            </div>

            <SliderControls
              scrollProgress={scrollProgress}
              canScrollLeft={canScrollLeft}
              canScrollRight={canScrollRight}
              onScroll={scrollByCards}
            />
          </div>
        </div>
      </section>
    </>
  );
};

export default AvailablePrograms;
